import { useEffect, useMemo, useState } from 'react';
import type { CatalogProviderSummary } from '@/features/catalog';

export type UseProviderSelectionResult = {
  selectedProviderId: number | null;
  selectedProvider?: CatalogProviderSummary;
  setSelectedProviderId: (providerId: number | null) => void;
};

export function useProviderSelection(
  providers: CatalogProviderSummary[],
  initialSelectedId?: number | null
): UseProviderSelectionResult {
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(
    initialSelectedId ?? providers[0]?.id ?? null
  );

  useEffect(() => {
    if (providers.length === 0) {
      setSelectedProviderId(null);
      return;
    }
    if (selectedProviderId === null) {
      setSelectedProviderId(providers[0]?.id ?? null);
      return;
    }
    const stillExists = providers.some(
      (provider) => provider.id === selectedProviderId
    );
    if (!stillExists) {
      setSelectedProviderId(providers[0]?.id ?? null);
    }
  }, [providers, selectedProviderId]);

  const selectedProvider = useMemo(() => {
    if (selectedProviderId === null) {
      return undefined;
    }
    return providers.find((provider) => provider.id === selectedProviderId);
  }, [providers, selectedProviderId]);

  return { selectedProviderId, selectedProvider, setSelectedProviderId };
}
